import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Sparkles, ArrowRight, X } from "lucide-react";

const STORAGE_KEY = "nexus_studio_tour_v1";

// Each step targets a data-testid inside MediaEditor. If the target isn't in the
// DOM (e.g. image mode, no Trim tab), we fall back to a centred callout.
const STEPS = [
  {
    id: "welcome",
    title: "Welcome to Studio",
    body: "You've got a full IG/TikTok-style editor built right in. Let's do a 20-second tour.",
    target: null,
    accent: "from-nexus-pink to-nexus-purple",
    duration: "20 seconds",
  },
  {
    id: "trim",
    title: "Trim + speed",
    body: "Cut your clip to a punchy 15 seconds. Speed it up (2×) for a highlight reel or slow it down for drama.",
    target: '[data-testid="editor-tab-trim"]',
    accent: "from-nexus-blue to-nexus-purple",
  },
  {
    id: "look",
    title: "Presets + fine-tune",
    body: "Six looks — Vivid, Vintage, Warm, Cool, B&W — plus brightness, contrast and saturation sliders when you need them.",
    target: '[data-testid="editor-tab-look"]',
    accent: "from-nexus-purple to-nexus-pink",
  },
  {
    id: "ai",
    title: "AI captions + subtitles",
    body: "Tap Generate caption and Claude writes an on-brand caption + hashtags + hooks. Auto-subtitles burn onto the video too.",
    target: '[data-testid="editor-tab-ai"]',
    accent: "from-nexus-pink to-amber-400",
  },
  {
    id: "export",
    title: "Export → uploaded",
    body: "Hit Export & upload — your edit is rendered right in the browser and lands straight in your portfolio.",
    target: '[data-testid="editor-export-btn"]',
    accent: "from-emerald-400 to-nexus-blue",
  },
];

export function hasSeenStudioTour() {
  try { return localStorage.getItem(STORAGE_KEY) === "seen"; }
  catch { return false; }
}

export function markStudioTourSeen() {
  try { localStorage.setItem(STORAGE_KEY, "seen"); } catch { /* ignore */ }
}

export function resetStudioTour() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

/**
 * Studio walk-through — shown the first time a creator opens the editor.
 * Renders as a Portal overlay above the MediaEditor's own portal.
 */
export default function StudioOnboarding({ mediaKind = "image", onClose }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);

  // Skip Trim step if we're editing an image (no Trim tab)
  const steps = STEPS.filter((s) => !(s.id === "trim" && mediaKind !== "video"));
  const step = steps[i];

  useEffect(() => {
    if (!step?.target) { setRect(null); return; }
    // Poll briefly for the target so we can highlight it (tabs render fast but not guaranteed synchronous)
    let cancelled = false;
    const find = () => {
      if (cancelled) return;
      const el = document.querySelector(step.target);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else {
        setTimeout(find, 100);
      }
    };
    find();
    const onResize = () => {
      const el = document.querySelector(step.target);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };
    window.addEventListener("resize", onResize);
    return () => { cancelled = true; window.removeEventListener("resize", onResize); };
  }, [step]);

  const finish = () => {
    markStudioTourSeen();
    onClose?.();
  };

  const next = () => {
    if (i >= steps.length - 1) return finish();
    setI(i + 1);
  };

  const skip = () => finish();

  // Position of the callout: near the highlighted target, otherwise centered
  const calloutStyle = (() => {
    if (!rect) return {}; // centered by default
    // Place callout to the LEFT of the target if there's room, else below
    const margin = 20;
    const calloutW = 340;
    let left = rect.left - calloutW - margin;
    let top = rect.top;
    if (left < margin) {
      // Not enough room — place below
      left = Math.max(margin, Math.min(window.innerWidth - calloutW - margin, rect.left));
      top = rect.top + rect.height + margin;
    }
    // Clamp to viewport
    top = Math.max(margin, Math.min(window.innerHeight - 260, top));
    return { top, left, width: calloutW };
  })();

  return createPortal(
    <div
      className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-[2px]"
      data-testid="studio-onboarding"
      role="dialog"
      aria-label="Studio walk-through"
    >
      {/* Cutout highlight around the target */}
      {rect && (
        <div
          className="absolute rounded-xl ring-2 ring-nexus-pink shadow-[0_0_0_9999px_rgba(0,0,0,0.35)] transition-all duration-300 pointer-events-none"
          style={{ top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12 }}
          data-testid="onboarding-cutout"
        />
      )}

      {/* Callout card */}
      <div
        className={`absolute glass rounded-2xl p-5 shadow-[0_20px_80px_rgba(0,0,0,0.6)] hairline ${rect ? "" : "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[340px]"}`}
        style={rect ? calloutStyle : {}}
        data-testid="onboarding-callout"
      >
        <button
          onClick={skip}
          className="absolute top-3 right-3 text-zinc-400 hover:text-white"
          data-testid="onboarding-skip-btn"
          aria-label="Skip tour"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-2 mb-3">
          <div className={`h-8 w-8 rounded-lg bg-gradient-to-br ${step.accent} flex items-center justify-center`}>
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">
              Step {i + 1} of {steps.length}
              {step.duration && <span className="ml-2 text-nexus-pink">· {step.duration}</span>}
            </div>
            <div className="font-display font-bold text-lg tracking-tight leading-none mt-1">{step.title}</div>
          </div>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">{step.body}</p>

        {/* Progress dots */}
        <div className="flex items-center gap-1.5 mt-4">
          {steps.map((_, idx) => (
            <span
              key={idx}
              className={`h-1 rounded-full transition-all ${idx === i ? "w-6 bg-nexus-pink" : idx < i ? "w-1.5 bg-nexus-pink/40" : "w-1.5 bg-white/15"}`}
            />
          ))}
        </div>

        <div className="flex items-center justify-between mt-5">
          <button
            onClick={skip}
            className="text-xs uppercase tracking-widest text-zinc-500 hover:text-white"
            data-testid="onboarding-skip-link"
          >
            Skip tour
          </button>
          <button
            onClick={next}
            className="inline-flex items-center gap-1.5 nexus-gradient text-white rounded-full h-9 px-4 font-semibold text-sm"
            data-testid="onboarding-next-btn"
          >
            {i >= steps.length - 1 ? "Start editing" : "Next"}
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
